import {THEME} from "@/enums/profile";
import {parseThemeCookieValue} from "@/utils/common/themeCookie";

export const UppercaseFirstLetter = (str: string) => {
   return (str || "").charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const SplitOnUnderscore = (str: string) => {
   return (str || "").split("_").join(" ");
};

export const SplitAndCapitalize = (str: string) => {
   return (str || "")
      .split("_")
      .map((word) => UppercaseFirstLetter(word))
      .join(" ");
};

export const SplitAndCapitalizeIfNoSpaces = (str: string) => {
   if (str.includes(" ")) {
      return str;
   }

   return (str || "")
      .split("_")
      .map((word) => UppercaseFirstLetter(word))
      .join(" ");
};

export const URL_REG_EXP = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/\S*)?$/;

export async function HashObject(obj: Record<string, any>): Promise<string> {
   const str = JSON.stringify(obj, Object.keys(obj).sort());
   const buffer = new TextEncoder().encode(str);
   const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
   return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
}

export enum SORT_ORDER {
   ASC = "asc",
   DESC = "desc",
}

export const GetTheme = (): THEME => {
   if (typeof window === "undefined") {
      return THEME.LIGHT;
   }

   // Prefer the SSR-synced cookie, then legacy localStorage.
   let theme: string | null = null;
   try {
      const match = document.cookie
         .split(";")
         .map((part) => part.trim())
         .find((part) => part.startsWith("gail_theme="));
      if (match) {
         theme = decodeURIComponent(match.slice("gail_theme=".length));
      }
   } catch {
      // ignore
   }
   if (!theme) {
      theme = window.localStorage?.getItem("theme") ?? null;
   }

   const preference = parseThemeCookieValue(theme);
   if (preference === THEME.DARK || preference === THEME.LIGHT) {
      return preference;
   }
   if (preference === THEME.SYSTEM) {
      return window?.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
         ? THEME.DARK
         : THEME.LIGHT;
   }

   return THEME.LIGHT;
};

export const GetSystemTheme = (): THEME.LIGHT | THEME.DARK => {
   return window?.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? THEME.DARK
      : THEME.LIGHT;
};
